/**
 * Web Navigation Facade
 * 
 * React Router implementation of the NavigationFacade interface.
 * This is the web-specific implementation that uses React Router's useNavigate.
 * 
 * Usage:
 * ```tsx
 * const navigation = useWebNavigation();
 * navigation.toConversation('123');
 * navigation.goBack();
 * ```
 * 
 * @module useWebNavigation
 * @platform Web
 */

import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, setPreviousPage } from '@ideoz/core';
import type { NavigationFacade } from '@ideoz/core';

/**
 * Web Navigation Hook
 * 
 * Provides navigation using React Router.
 * Implements the NavigationFacade interface.
 */
export function useWebNavigation(): NavigationFacade {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  return {
    toConversation: useCallback(
      (id?: string, replace?: boolean) => {
        // Check if we're coming from project details
        // If so, use replace to not add conversation to history
        const isFromProjectDetails = location.pathname.startsWith('/projects/');
        // Default to replace=true when coming from project details, otherwise use the provided value
        const shouldReplace = replace !== undefined ? replace : isFromProjectDetails;
        
        // Pass state to indicate we came from project details (for restoration on back)
        const state = isFromProjectDetails ? { fromProjectDetails: true } : undefined;
        const options = { replace: shouldReplace, state };
        if (id) {
          navigate(`/conversation/${id}`, options);
        } else {
          navigate('/conversation', options);
        }
      },
      [navigate, location.pathname]
    ),

    toProjectDetails: useCallback(
      (id: string, replace?: boolean) => {
        // Check if we're currently on a conversation route
        // If so, always use replace to restore the project details that was replaced
        const currentPath = location.pathname;
        const isOnConversationRoute = currentPath.startsWith('/conversation');
        
        // If replace is explicitly provided, use it. Otherwise, use replace if we're on conversation route
        const shouldReplace = replace !== undefined ? replace : isOnConversationRoute;
        
        // Determine previous page from current location (only if not replacing)
        if (!shouldReplace) {
          const previousPage: 'landing' | 'projects' = currentPath === '/' || currentPath === '/landing' ? 'landing' : 'projects';
          dispatch(setPreviousPage(previousPage));
        }
        
        // Always use replace when coming from conversation to restore the replaced project details entry
        const options = shouldReplace ? { replace: true } : {};
        navigate(`/projects/${id}`, options);
      },
      [navigate, dispatch, location.pathname]
    ),

    toProjectsList: useCallback(
      () => {
        // Old /projects list is retired — point at My Startups instead.
        navigate('/startups');
      },
      [navigate]
    ),

    toProfile: useCallback(
      () => {
        navigate('/profile');
      },
      [navigate]
    ),

    toBilling: useCallback(
      () => {
        navigate('/billing');
      },
      [navigate]
    ),

    toAppSettings: useCallback(
      () => {
        navigate('/app-settings');
      },
      [navigate]
    ),

    toIntegrations: useCallback(
      () => {
        navigate('/integrations');
      },
      [navigate]
    ),

    toReportProblem: useCallback(
      () => {
        navigate('/report-problem');
      },
      [navigate]
    ),

    toLanding: useCallback(
      () => {
        navigate('/');
      },
      [navigate]
    ),

    goBack: useCallback(
      () => {
        // Check current location state to see if we came from project details
        // This helps us skip conversation routes when going back from project details
        const currentState = location.state as { fromProjectDetails?: boolean } | null;
        const currentPath = location.pathname;
        
        if (currentPath.startsWith('/projects/')) {
          // We're on project details page
          // Check if we have history length > 1, and try to skip conversation routes
          // Since conversation uses replace, we should be able to go back normally
          // But we'll go back and check if we land on conversation
          navigate(-1);
        } else {
          // Normal back navigation for other pages
          navigate(-1);
        }
      },
      [navigate, location.pathname, location.state]
    ),
  };
}
